/**
 * Registration Page for Partner Nest Matrimonial
 * Comprehensive registration form with fixed UserID consistency
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, CheckCircle } from 'lucide-react';
import { API_CONFIG } from '../config/api.config';

// Caste data organized by religion
const casteData = {
  hindu: [
    "Agarwal", "Ahir", "Arora", "Baidya", "Baniya", "Bhumihar", "Brahmin", 
    "Chamar", "Chasa", "Dhiman", "Dhobi", "Gavli", "Goud", "Gowda", 
    "Gurjar", "Jat", "Kalar", "Kamboj", "Kansari", "Kapu", "Karana", 
    "Karmakar", "Kashyap", "Kayastha", "Khandayat", "Khatri", "Koli", 
    "Komat", "Konar", "Kshatriya", "Kumhar", "Kurmi", "Kuruba", 
    "Lingayath", "Lohar", "Madiga", "Mahar", "Mahishya", "Mala", 
    "Mali", "Maratha", "Meena", "Mochi", "Mudaliar", "Nadar", "Nai", 
    "Naidu", "Nair", "Namasudra", "Nayak", "Oraon", "Padmashali", 
    "Pal", "Panchal", "Pandit", "Pasi", "Patel", "Patnaik", "Pillai", 
    "Prajapati", "Rajaka", "Rajput", "Reddy", "Saha", "Sahu", "Saini", 
    "Saliya", "Senai Thalaivar", "Senguntha Mudaliar", "Setty Balija", 
    "Shimpi", "Sindhi", "Sonar", "Soni", "Sourashtra", "Srisayani", 
    "Sundhi", "Suthar", "Swakula Sali", "Taga", "Teli", "Thakkar", 
    "Thakur", "Thevar", "Tili", "Uppara", "Vaddera", "Vaish", 
    "Vaishnav", "Vaishya", "Valluvan", "Valmiki", "Vania", "Vannar", 
    "Vanniyar", "Varshney", "Vellalar", "Velama", "Vishwakarma", 
    "Vokkaliga", "Yadav", "Other"
  ],
  muslim: [
    "Ansari", "Arain", "Awan", "Bohra", "Dekkani", "Dudekula", 
    "Jat", "Khoja", "Labbay", "Mappila", "Memon", "Mughal", 
    "Pathan", "Qureshi", "Rajput", "Shafi", "Shaikh", "Siddiqui", 
    "Syed", "Other"
  ],
  christian: [
    "Anglo-Indian", "Born Again", "Catholic", "Jacobite", 
    "Knanaya", "Malankara", "Marthoma", "Orthodox", "Pentecost", 
    "Protestant", "Roman Catholic", "Syrian Christian", 
    "Syro-Malabar", "Other"
  ],
  sikh: [
    "Arora", "Bhatia", "Jat", "Kamboj", "Khatri", "Majhabi", 
    "Ramdasia", "Ramgharia", "Saini", "Tarkhan", "Other"
  ],
  jain: [
    "Agarwal", "Bania", "Digambar", "Humbad", "Khandelwal", 
    "Kesari", "Khatri", "KVO", "Mahajan", "Osval", "Parwar", 
    "Shrimal", "Shwetambar", "Terapanthi", "Vaish", "Other"
  ],
  buddhist: [
    "Mahayana", "Theravada", "Vajrayana", "Zen", "Other"
  ],
  parsi: [
    "Irani", "Other"
  ],
  jewish: [
    "Ashkenazi", "Mizrahi", "Sephardic", "Other"
  ],
  bahai: [
    "No specific castes", "Other"
  ]
};

export default function Registration() {
  const [formData, setFormData] = useState({
    // Basic Details
    profileFor: '',
    firstName: '',
    lastName: '',
    mobileNo: '',
    email: '',
    profession: '',
    password: '',
    confirmPassword: '',
    registeredBy: '',
    relation: '',

    // Personal Details
    dob: '',
    gender: '',
    religion: '',
    caste: '',
    motherTongue: '',
    maritalStatus: '',
    height: '',
    weight: '',

    // Agreements
    termsAccepted: false,
    privacyAccepted: false
  });

  const [availableCastes, setAvailableCastes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  // Function to generate unique UserID - SINGLE SOURCE OF TRUTH
  const generateUserId = () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `PN${timestamp}${random}`;
  };

  // Update available castes when religion changes
  useEffect(() => {
    if (formData.religion) {
      // Convert religion to lowercase to match casteData keys (hindu, muslim, christian, etc.)
      const religionKey = formData.religion.toLowerCase() as keyof typeof casteData;
      const castes = casteData[religionKey];
      
      if (castes && castes.length > 0) {
        setAvailableCastes(castes);
        setFormData(prev => ({ ...prev, caste: '' }));
      } else {
        setAvailableCastes([]);
        setFormData(prev => ({ ...prev, caste: '' }));
      }
    } else {
      setAvailableCastes([]);
      setFormData(prev => ({ ...prev, caste: '' }));
    }
  }, [formData.religion]);

  // Check if profile is for someone else (not self)
  // Case-insensitive comparison to handle 'Self', 'self', or 'SELF'
  const isProfileForOthers = formData.profileFor && formData.profileFor.toLowerCase() !== 'self';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate required fields when profile is for others
    if (isProfileForOthers) {
      if (!formData.registeredBy.trim()) {
        alert('Please enter the name of the person registering the profile');
        setLoading(false);
        return;
      }
      if (!formData.relation) {
        alert('Please select your relation with the profile candidate');
        setLoading(false);
        return;
      }
    }

    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match!');
      setLoading(false);
      return;
    }

    if (!formData.termsAccepted ) {
      alert('Please accept the terms and conditions');
      setLoading(false);
      return;
    }

    // Validate Date of Birth vs current date (age rules)
    if (!formData.dob) {
      setErrorMessage('Please enter date of birth');
      setShowErrorModal(true);
      setLoading(false);
      return;
    }

    const calculateAge = (dobStr: string) => {
      const dobDate = new Date(dobStr);
      const today = new Date();
      let age = today.getFullYear() - dobDate.getFullYear();
      const m = today.getMonth() - dobDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) {
        age--;
      }
      return age;
    };

    const age = calculateAge(formData.dob);
    // Male requires >=21, others (Female and unspecified) require >=18
    const minAge = formData.gender === 'Male' ? 21 : 18;

    if (age < minAge) {
      setErrorMessage('Marriage eligibility: Female must be 18 years or older, and Males must be 21 years or older. Please enter a valid date of birth.');
      setShowErrorModal(true);
      setLoading(false);
      return;
    }

    // Generate UserID ONCE - SINGLE SOURCE OF TRUTH
    const generatedUserId = generateUserId();
    console.log('🆔 Generated UserID (Single Source):', generatedUserId);

    const userData = {
      userId: generatedUserId,
      mobile: formData.mobileNo,
      basicDetails: {
        profileFor: formData.profileFor,
        firstName: formData.firstName,
        lastName: formData.lastName,
        mobileNo: formData.mobileNo,
        email: formData.email,
        profession: formData.profession,
        password: formData.password,
        ...(isProfileForOthers && {
          registeredBy: formData.registeredBy,
          relation: formData.relation
        })
      },
      personalDetails: {
        dob: new Date(formData.dob),
        gender: formData.gender,
        religion: formData.religion,
        caste: formData.caste,
        motherTongue: formData.motherTongue,
        maritalStatus: formData.maritalStatus,
        height: parseFloat(formData.height) || 0,
        weight: parseInt(formData.weight) || 0
      }
    };

    console.log('📤 Sending registration with UserID:', generatedUserId);
    
    try {
      // Use the FIXED registration endpoint
      const response = await fetch(API_CONFIG.getApiEndpoint('fixed-registration/register-fixed'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const result = await response.json();
      
      if (result.success) {
        const confirmedUserId = result.userId || generatedUserId;
        console.log('✅ Registration successful, creating folder with UserID:', confirmedUserId);
        
        // Create user-specific folder structure using the backend-confirmed UserID
        try {
          const folderResponse = await fetch(API_CONFIG.getApiEndpoint('media-folders-direct/create'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userId: confirmedUserId
            }),
          });

          const folderResult = await folderResponse.json();
          
          if (folderResult.success) {
            console.log('✅ User folder structure created successfully:', folderResult);
            alert(`Registration successful! Your profile is under verification. User folder (${confirmedUserId}) created in Media folder. You will be redirected to login.`);
          } else {
            console.warn('⚠️ User folder creation failed:', folderResult.message);
            alert(`Registration successful! Your profile is under verification. (Note: User folder creation failed - ${folderResult.message})`);
          }
        } catch (error) {
          console.error('❌ Error creating user folders:', error);
          alert('Registration successful! Your profile is under verification. (Note: User folder creation encountered an error)');
        }
        
        // Navigate to login page after successful registration
        setTimeout(() => {
          navigate('/login');
        }, 2000);
        
        // Reset form
        setFormData({
          profileFor: '',
          firstName: '',
          lastName: '',
          mobileNo: '',
          email: '',
          profession: '',
          password: '',
          confirmPassword: '',
          registeredBy: '',
          relation: '',
          dob: '',
          gender: '',
          religion: '',
          caste: '',
          motherTongue: '',
          maritalStatus: '',
          height: '',
          weight: '',
          termsAccepted: false,
          privacyAccepted: false,
        });
      } else {
        setErrorMessage(result.message || 'Registration failed');
        setShowErrorModal(true);
      }
    } catch (error: any) {
      console.error('❌ Registration error:', error);
      setErrorMessage('Registration failed: ' + error.message);
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br bg-gradient-to-r from-[#1e3a8a] via-[#7c3aed] to-[#ec4899] py-10 text-white">
      <div className="container mx-auto px-4 max-w-4xl">

        <Card className="border-2 border-white/10 bg-[#5f1c7c] shadow-2xl backdrop-blur-xl">
          <CardHeader className="bg-gradient-to-r from-[#1e3a8a] via-[#7c3aed] to-[#ec4899] text-white rounded-t-3xl shadow-lg">
            <CardTitle className="text-3xl text-[#ffd9a6] text-center tracking-tight">
              Create Your Partner Nest Profile
            </CardTitle>
            <p className="text-[#ffd9a6] text-center mt-2">
              Start your journey to find the perfect match
            </p>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Basic Details Section */}
              <section className="bg-gradient-to-r from-[#1e3a8a] via-[#7c3aed] to-[#ec4899] p-6 rounded-2xl">
                <h3 className="text-xl font-semibold text-[#ffd9a6] mb-4 border-b-2 border-white/20 pb-2">
                  Basic Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="profileFor" className="text-[#ffd9a6]">Profile For</Label>
                    <Select 
                      onValueChange={(value) => setFormData({...formData, profileFor: value, registeredBy: '', relation: ''})}
                      disabled={loading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select profile for" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Self">Self</SelectItem>
                        <SelectItem value="Son">Son</SelectItem>
                        <SelectItem value="Daughter">Daughter</SelectItem>
                        <SelectItem value="Brother">Brother</SelectItem>
                        <SelectItem value="Sister">Sister</SelectItem>
                        <SelectItem value="Relative">Relative</SelectItem>
                        <SelectItem value="Friend">Friend</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Registered By and Relation Fields - Conditionally shown */}
                  {isProfileForOthers && (
                    <>
                      <div>
                        <Label htmlFor="registeredBy" className="text-[#ffd9a6]">
                          Registered by <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="registeredBy"
                          placeholder="Enter your name"
                          value={formData.registeredBy}
                          onChange={(e) => setFormData({...formData, registeredBy: e.target.value})}
                          required={isProfileForOthers}
                          disabled={loading}
                        />
                      </div>

                      <div>
                        <Label htmlFor="relation" className="text-[#ffd9a6]">
                          Relation <span className="text-red-500">*</span>
                        </Label>
                        <Select 
                          onValueChange={(value) => setFormData({...formData, relation: value})}
                          disabled={loading}
                          required={isProfileForOthers}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select relation" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Father">Father</SelectItem>
                            <SelectItem value="Mother">Mother</SelectItem>
                            <SelectItem value="Brother">Brother</SelectItem>
                            <SelectItem value="Sister">Sister</SelectItem>
                            <SelectItem value="Uncle">Uncle</SelectItem>
                            <SelectItem value="Aunt">Aunt</SelectItem>
                            <SelectItem value="Guardian">Guardian</SelectItem>
                            <SelectItem value="Friend">Friend</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  <div>
                    <Label htmlFor="firstName" className="text-[#ffd9a6]">First Name</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="lastName" className="text-[#ffd9a6]">Last Name</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="mobileNo" className="text-[#ffd9a6]">Mobile No</Label>
                    <Input
                      id="mobileNo"
                      type="tel"
                      value={formData.mobileNo}
                      onChange={(e) => setFormData({...formData, mobileNo: e.target.value})}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-[#ffd9a6]">Email ID</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="profession" className="text-[#ffd9a6]">Profession</Label>
                    <Select 
                      onValueChange={(value) => setFormData({...formData, profession: value})}
                      disabled={loading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select profession" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Software Engineer">Software Engineer</SelectItem>
                        <SelectItem value="Doctor">Doctor</SelectItem>
                        <SelectItem value="Engineer">Engineer</SelectItem>
                        <SelectItem value="Teacher">Teacher</SelectItem>
                        <SelectItem value="Business">Business</SelectItem>
                        <SelectItem value="Government Employee">Government Employee</SelectItem>
                        <SelectItem value="Private Employee">Private Employee</SelectItem>
                        <SelectItem value="Student">Student</SelectItem>
                        <SelectItem value="Lawyer">Lawyer</SelectItem>
                        <SelectItem value="CA/Accountant">CA/Accountant</SelectItem>
                        <SelectItem value="Architect">Architect</SelectItem>
                        <SelectItem value="Designer">Designer</SelectItem>
                        <SelectItem value="Marketing">Marketing</SelectItem>
                        <SelectItem value="Sales">Sales</SelectItem>
                        <SelectItem value="Banking">Banking</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="password" className="text-[#ffd9a6]">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword" className="text-[#ffd9a6]">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
              </section>

              {/* Personal Details Section */}
              <section className="bg-gradient-to-r from-[#1e3a8a] via-[#7c3aed] to-[#ec4899] p-6 rounded-2xl">
                <h3 className="text-xl font-semibold text-[#ffd9a6] mb-4 border-b-2 border-white/20 pb-2">
                  Personal Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="dob" className="text-[#ffd9a6]">Date of Birth</Label>
                    <Input
                      id="dob"
                      type="date"
                      value={formData.dob}
                      onChange={(e) => setFormData({...formData, dob: e.target.value})}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="gender" className="text-[#ffd9a6]">Gender</Label>
                    <Select 
                      onValueChange={(value) => setFormData({...formData, gender: value})}
                      disabled={loading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="religion" className="text-[#ffd9a6]">Religion</Label>
                    <Select 
                      onValueChange={(value) => setFormData({...formData, religion: value})}
                      disabled={loading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select religion" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Hindu">Hindu</SelectItem>
                        <SelectItem value="Muslim">Muslim</SelectItem>
                        <SelectItem value="Christian">Christian</SelectItem>
                        <SelectItem value="Sikh">Sikh</SelectItem>
                        <SelectItem value="Jain">Jain</SelectItem>
                        <SelectItem value="Buddhist">Buddhist</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="caste" className="text-[#ffd9a6]">Caste</Label>
                    <Select 
                      onValueChange={(value) => setFormData({...formData, caste: value})}
                      disabled={loading || !formData.religion}
                      value={formData.caste}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={formData.religion ? "Select caste" : "Select religion first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCastes.length > 0 ? (
                          availableCastes.map((caste) => (
                            <SelectItem key={caste} value={caste}>
                              {caste}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-caste-available" disabled>
                            {formData.religion ? "No castes available for this religion" : "Please select religion first"}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="motherTongue" className="text-[#ffd9a6]">Mother Tongue</Label>
                    <Select 
                      onValueChange={(value) => setFormData({...formData, motherTongue: value})}
                      disabled={loading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select mother tongue" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Hindi">Hindi</SelectItem>
                        <SelectItem value="Bengali">Bengali</SelectItem>
                        <SelectItem value="Telugu">Telugu</SelectItem>
                        <SelectItem value="Marathi">Marathi</SelectItem>
                        <SelectItem value="Tamil">Tamil</SelectItem>
                        <SelectItem value="Urdu">Urdu</SelectItem>
                        <SelectItem value="Gujarati">Gujarati</SelectItem>
                        <SelectItem value="Kannada">Kannada</SelectItem>
                        <SelectItem value="Odia">Odia</SelectItem>
                        <SelectItem value="Punjabi">Punjabi</SelectItem>
                        <SelectItem value="Malayalam">Malayalam</SelectItem>
                        <SelectItem value="Assamese">Assamese</SelectItem>
                        <SelectItem value="Maithili">Maithili</SelectItem>
                        <SelectItem value="Sanskrit">Sanskrit</SelectItem>
                        <SelectItem value="Kashmiri">Kashmiri</SelectItem>
                        <SelectItem value="Sindhi">Sindhi</SelectItem>
                        <SelectItem value="Konkani">Konkani</SelectItem>
                        <SelectItem value="Nepali">Nepali</SelectItem>
                        <SelectItem value="Manipuri">Manipuri</SelectItem>
                        <SelectItem value="Bodo">Bodo</SelectItem>
                        <SelectItem value="Dogri">Dogri</SelectItem>
                        <SelectItem value="Santali">Santali</SelectItem>
                        <SelectItem value="English">English</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="maritalStatus" className="text-[#ffd9a6]">Marital Status</Label>
                    <Select 
                      onValueChange={(value) => setFormData({...formData, maritalStatus: value})}
                      disabled={loading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select marital status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Never Married">Never Married</SelectItem>
                        <SelectItem value="Divorced">Divorced</SelectItem>
                        <SelectItem value="Widowed">Widowed</SelectItem>
                        <SelectItem value="Separated">Separated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="height" className="text-[#ffd9a6]">Height (cm)</Label>
                    <Input
                      id="height"
                      type="number"
                      value={formData.height}
                      onChange={(e) => setFormData({...formData, height: e.target.value})}
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="weight" className="text-[#ffd9a6]">Weight (kg)</Label>
                    <Input
                      id="weight"
                      type="number"
                      value={formData.weight}
                      onChange={(e) => setFormData({...formData, weight: e.target.value})}
                      disabled={loading}
                    />
                  </div>
                </div>
              </section>

              {/* Agreements Section */}
              <section className="bg-gradient-to-r from-[#1e3a8a] via-[#7c3aed] to-[#ec4899] p-6 rounded-2xl">
                <h3 className="text-xl font-semibold text-[#ffd9a6] mb-4 border-b-2 border-white/20 pb-2">
                  Agreements
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="terms"
                      checked={formData.termsAccepted}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, termsAccepted: checked as boolean }))}
                      disabled={loading}
                      className="border-[#ffd9a6] data-[state=checked]:bg-[#ffd9a6] data-[state=checked]:border-[#ffd9a6] data-[state=checked]:text-[#0f172a]"
                    />

                    <Label htmlFor="terms" className="text-[#ffd9a6] text-sm">
                      I agree to the Privacy Policy and Terms & Conditions of Partner Nest Matrimonial
                    </Label>

                  </div>

                </div>
              </section>

              {/* Submit Button */}
              <div className="text-center">
                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-[#1e3a8a] via-[#7c3aed] to-[#ec4899] hover:from-[#4b186c] hover:to-[#e7207d] text-white px-12 py-3 text-lg"
                  disabled={!formData.termsAccepted  || loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Profile...
                    </>
                  ) : (
                    'Create Profile'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>


      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md bg-[#5f1c7c] rounded-3xl shadow-2xl border border-white/10">
            <div className="bg-gradient-to-r from-[#ff2d87] via-[#5f1c7c] to-[#0b3b82] text-white text-center p-6 rounded-t-3xl">
              <h2 className="text-xl font-bold">Error</h2>
            </div>
            
            <div className="p-6 text-slate-200">
              <p>{errorMessage}</p>
            </div>

            <div className="flex justify-center p-6 border-t border-white/10">
              <button
                onClick={() => setShowErrorModal(false)}
                className="py-3 px-6 bg-gradient-to-r from-[#5f1c7c] via-[#0b3b82] to-[#ff2d87] text-white rounded-2xl"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}